/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
/* eslint-disable security/detect-non-literal-regexp */
import { PriceComponent } from '@msdyn365-commerce/components';
import { getRichTextHtml, IActionContext, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import { format, ProductPrice, SearchSuggestion, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import classnames from 'classnames';

import { IDlvrySearchData } from '../dlvry-search.data';
import { IDlvrySearchProps, IDlvrySearchResources } from '../dlvry-search.props.autogenerated';
import BuySection from './buy-section';
import PQueue from 'p-queue';
import { BuyValidationResult } from '../../../themes/dlvry-adventureworks/views/components/product.component';
import { multiplyPriceBy } from '../../../shared-utils/price-utils';
import { humanizeUnitOfMeasure } from '../../../components/unit-of-measure';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { formatCurrencyNOK } from '../../../shared-utils/common-utils';

/**
 * Search product suggest resources.
 */
export interface ISearchProductSuggestResources {
    freePriceText: string;
    inCartTooltipText: string;
    pricePerText: string;
}

export interface ISearchProductSuggestProps {
    searchProductSuggestClass: string;
    searchPageURL: string;
    searchQsp: string | undefined;
    hitPrefix: string;
    hitSuffix: string;
    autoSuggestProps: IDlvrySearchProps<IDlvrySearchData>;
    searchText: string;
    suggestions: SearchSuggestion[] | undefined;
    telemetry: ITelemetry;
    moduleType: string;
    moduleId: string;
    telemetryContent?: ITelemetryContent;
    locale: string;
    resources?: ISearchProductSuggestResources;
    activePrices: { [key: string]: ProductPrice };
    simpleProducts: SimpleProduct[];
    cart: ICartState | undefined;
    cartQueue: PQueue | undefined;
    unitConversions: UnitOfMeasureConversion[] | undefined;
    quantities: Map<number, number>;
    breakoffs: Map<number, boolean>;
    onQuantityChange: (productRecordId: number, quantity: number) => void;
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void;
    onBuyValidate: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
}

export interface ISearchProductViewProps {
    title: React.ReactNode;
    items: ISearchProductItemViewProps[];
}

export interface ISearchProductItemViewProps {
    text: React.ReactNode;
    thumbnail: React.ReactNode;
    price: React.ReactNode;
    buySection: React.ReactNode;
    LiProduct: INodeProps;
    AProduct: INodeProps;
    id: string | undefined;
}

let payLoad: IPayLoad;
let telemetryContent: ITelemetryContent | undefined;

/**
 * ProductSuggestionsComponent.
 * @param props
 */
export const ProductSuggestionsComponent = (props: ISearchProductSuggestProps): ISearchProductViewProps | undefined => {
    const {
        suggestions,
        hitSuffix,
        hitPrefix,
        autoSuggestProps: {
            context: { actionContext },
            resources: { productSuggestionHeading }
        },
        moduleType,
        moduleId,
        quantities,
        breakoffs,
        onQuantityChange,
        onBreakoffChange
    } = props;
    const reactNodes: ISearchProductItemViewProps[] = [];
    telemetryContent = props.telemetryContent;
    payLoad = getPayloadObject('click', props.telemetryContent!, '');

    suggestions?.map(item => {
        const quantity = quantities.get(parseInt(item.Id!, 10)) || 0;
        const breakoff = breakoffs.get(parseInt(item.Id!, 10)) || false;
        reactNodes.push(_assembleNode(item, hitPrefix, hitSuffix, actionContext, props, moduleType, moduleId, quantity, breakoff, onQuantityChange, onBreakoffChange));
    });

    const titleNode = _renderTitle(productSuggestionHeading);

    return { title: titleNode, items: reactNodes };
};

const _renderTitle = (productSuggestionHeading: string) => {
    const productAutoSuggestionHeading = productSuggestionHeading ? productSuggestionHeading : 'Go to products';

    return <li className='msc-autoSuggest__productResults-title'>{productAutoSuggestionHeading}</li>;
};

const _assembleNode = (
    item: SearchSuggestion,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps,
    moduleType: string,
    moduleId: string,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
): ISearchProductItemViewProps => {
    const altText =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    const price = props.activePrices[item.Id!];
    const simpleProduct = props.simpleProducts.find(product => product.RecordId === parseInt(item.Id!, 10));

    return {
        text: _getHighlightedText(item.Value ? item.Value : '', hitPrefix, hitSuffix),
        thumbnail: _renderThumbnail(item, altText ? altText : '', props),
        price: _renderPrice(price, simpleProduct, moduleId, moduleType, props, quantity, breakoff),
        buySection: _renderBuySection(item.Id!, props, quantity, breakoff, onQuantityChange, onBreakoffChange),
        LiProduct: _getLiWrapper(),
        AProduct: _getAWrapper(item, hitPrefix, hitSuffix, actionContext, props),
        id: item.Id
    };
};

const _getLiWrapper = () => {
    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'li' as NodeTag
    };
};

const _getAWrapper = (
    item: SearchSuggestion,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps
) => {
    const ariaLabel =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    payLoad.contentAction.recid = item.Id;
    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
    const productPrice = item.Value3?.Value?.DecimalValue ? formatCurrencyNOK(item.Value3.Value.DecimalValue) : undefined;

    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'a' as NodeTag,
        'aria-label': `${ariaLabel!} ${productPrice || ''}`,
        href: _generateLink(item, hitPrefix, hitSuffix, actionContext),
        ...attribute
    };
};

const _generateLink = (suggestion: SearchSuggestion, hitPrefix: string, hitSuffix: string, actionContext: IActionContext) => {
    const formattedLink = (suggestion.Value as string)
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');
    return suggestion.Value && suggestion.Id
        ? getProductPageUrlSync(formattedLink, (suggestion.Id as unknown) as number, actionContext, undefined)
        : '';
};

const _getHighlightedText = (suggestion: string, hitPrefix: string, hitSuffix: string): JSX.Element => {
    const formattedText = suggestion
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '<strong>')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '</strong>');

    return (
        // eslint-disable-next-line react/no-danger
        <span className='msc-price msc-autoSuggest__productResults-item-text' dangerouslySetInnerHTML={getRichTextHtml(formattedText)} />
    );
};

const _renderPrice = (
    price: ProductPrice | undefined,
    simpleProduct: SimpleProduct | undefined,
    moduleId: string,
    moduleType: string,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean
): JSX.Element | null => {
    const {
        autoSuggestProps: { context, resources }
    } = props;

    if (!context.request.user.isAuthenticated) {
        return (
            <div className='msc-autoSuggest__productResults-prices not-logged-in'>
                <span className='msc-price msc-autoSuggest__productResults-item-price' style={{ marginTop: 2 }}>
                    {resources.priceNotLoggedInText}
                </span>
            </div>
        );
    }

    if (!price || !simpleProduct) {
        return (
            <div className='msc-autoSuggest__productResults-prices'>
                <span className='msc-price msc-autoSuggest__productResults-item-price'>
                    <span className='msc-price__actual retrieving'>
                        ...
                    </span>
                </span>
            </div>
        );
    }

    let productPrice = price;

    if (!breakoff && simpleProduct.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    return (
        <div className='msc-autoSuggest__productResults-prices'>
            <PriceComponent
                id={moduleId}
                typeName={moduleType}
                context={context}
                data={{
                    price: {
                        CustomerContextualPrice: productPrice.CustomerContextualPrice
                    }
                }}
                className='msc-autoSuggest__productResults-item-price'
                freePriceText={props.resources?.freePriceText}
            />
            {!breakoff && !!simpleProduct.Behavior?.MultipleOfQuantity && (
                <div className='msc-autoSuggest__productResults-item-price-per__wrapper'>
                    <PriceComponent
                        id={moduleId}
                        typeName={moduleType}
                        context={context}
                        data={{
                            price: {
                                CustomerContextualPrice: price.CustomerContextualPrice
                            }
                        }}
                        className='msc-autoSuggest__productResults-item-price-per'
                        freePriceText={props.resources?.freePriceText}
                    />
                    <span>
                        &nbsp;
                        {format(props.resources?.pricePerText || 'pr. {0}', humanizeUnitOfMeasure(props.locale, simpleProduct.DefaultUnitOfMeasure, simpleProduct))}
                    </span>
                </div>
            )}
            <div className={classnames('msc-autoSuggest__productResults-total-price-wrapper', { retrieving: price === undefined })}>
                <div className='msc-autoSuggest__productResults-total-price-text'>
                    Totalt
                </div>
                <PriceComponent
                    context={context}
                    id={moduleId}
                    typeName={moduleType}
                    className='msc-autoSuggest__productResults-total-price-price'
                    data={{ price: { CustomerContextualPrice: (price.CustomerContextualPrice || 0) * quantity } }}
                    freePriceText=' '
                />
            </div>
        </div>
    );
};

const _renderBuySection = (
    itemId: string,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
): JSX.Element | null => {
    const productId = parseInt(itemId, 10);

    if (isNaN(productId) || !props.cart) {
        return null;
    }

    if (!props.autoSuggestProps.context.request.user.isAuthenticated) {
        return null;
    }

    const {
        id,
        typeName,
        context
    } = props.autoSuggestProps;
    const simpleProduct = props.simpleProducts.find(product => product.RecordId === productId);
    const productPrice = props.activePrices[itemId];

    return (
        <BuySection
            id={id}
            typeName={typeName}
            context={context}
            resources={props.resources as IDlvrySearchResources}
            productRecordId={productId}
            simpleProduct={simpleProduct}
            productPrice={productPrice}
            cart={props.cart}
            cartQueue={props.cartQueue}
            onBuyValidate={props.onBuyValidate}
            inCartTooltipText={props.resources?.inCartTooltipText}
            unitConversions={props.unitConversions?.filter(x => x.ProductRecordId === productId) || []}
            quantity={quantity}
            breakoff={breakoff}
            onQuantityChange={onQuantityChange}
            onBreakoffChange={onBreakoffChange}
        />
    );
};

const _renderThumbnail = (suggestion: SearchSuggestion, altText: string, props: ISearchProductSuggestProps): JSX.Element | null => {
    const {
        autoSuggestProps: {
            context: {
                request: { gridSettings }
            },
            config: { imageSettings }
        }
    } = props;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }

    if (!suggestion || !suggestion.ImageUrl) {
        props.telemetry.error('Search suggestion content is empty, module wont render.');
        return null;
    }

    const defaultImageSettings: IImageSettings = {
        viewports: {
            lg: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            md: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            sm: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            xl: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            xs: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    if (imageSettings) {
        imageSettings.viewports = defaultImageSettings.viewports;
    }

    return (
        <Image
            requestContext={props.autoSuggestProps.context.actionContext.requestContext}
            altText={altText}
            className='msc-autoSuggest__productResults-item-img'
            src={suggestion.ImageUrl}
            gridSettings={gridSettings!}
            imageSettings={imageSettings ? imageSettings : defaultImageSettings}
            loadFailureBehavior='empty'
        />
    );
};
